import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Download,
  ExternalLink,
  FileText,
  Filter,
  FolderOpen,
  ImageIcon,
  Link2,
  Loader2,
  PlayCircle,
  Search,
  type LucideIcon,
} from "lucide-react";
import {
  RESOURCE_KIND_LABELS,
  RESOURCE_ROUTES,
  resourceListResponseSchema,
  type Resource,
  type ResourceKind,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Input, Select } from "@/shared/ui/core";
import { EmptyState, SkeletonCard } from "@/shared/ui/ui";
import { formatOrderDate } from "@/features/admin/orders/format";
import { downloadResource, getResourceFile } from "@/features/resources/resources.api";

const KIND_ICONS: Record<ResourceKind, LucideIcon> = {
  video: PlayCircle,
  pdf: FileText,
  imagen: ImageIcon,
  link: Link2,
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Recursos — la BIBLIOTECA del sistema anterior, replicada (Anahí,
 * 27-ago): buscador y filtro arriba y cada material como una CARD con su
 * portada, el velo oscuro abajo con título, tipo, fecha y el icono de
 * descarga. Sin portada, un placeholder de la casa (bruma + icono del
 * tipo) — nunca una imagen rota.
 *
 * El filtro es por TIPO (Anahí, 28-ago: fuera el campo "Categoría" del
 * alta). El dato de categoría de los recursos importados sigue guardado
 * por si un día vuelve a servir, pero ya no lo pide nadie ni se pinta.
 *
 * Compartida entre el panel de socio y el de franquiciatario. Lee
 * `/api/resources`: SOLO lo activo.
 * ═══════════════════════════════════════════════════════════════════
 */
export function RecursosPage() {
  const { t } = useTranslation();
  const resources = useQuery({
    queryKey: ["me", "resources"],
    queryFn: async () => {
      const data = await authFetch(RESOURCE_ROUTES.list, resourceListResponseSchema, {
        baseUrl: "",
      });
      return data.items;
    },
  });

  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");

  const items = resources.data ?? [];
  /*
   * El filtro es por TIPO y ya no por categoría (Anahí, 28-ago: fuera el
   * campo "Categoría" del alta). Sale del propio recurso, así que nadie
   * tiene que acordarse de escribirlo bien —"Trípticos" y "tripticos"
   * eran dos grupos— y solo se ofrecen los tipos que de verdad hay.
   */
  const kinds = useMemo(() => [...new Set(items.map((item) => item.kind))], [items]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const inKind = !kind || item.kind === kind;
      const inSearch =
        !term ||
        item.title.toLowerCase().includes(term) ||
        (item.description ?? "").toLowerCase().includes(term);
      return inKind && inSearch;
    });
  }, [items, search, kind]);


  return (
    <>
      <PageHeader
        titleHidden
        title={t("Recursos")}
        description={t("Formación y materiales para hacer crecer tu negocio.")}
      />

      {/* ── Buscador y filtro, una fila limpia ────────────────────────── */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Buscar…")}
            aria-label={t("Buscar recursos")}
            className="pl-10"
          />
        </div>
        {kinds.length > 1 && (
          <div className="w-52">
            <Select
              aria-label={t("Tipo")}
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              placeholder={t("Todos los tipos")}
              options={kinds.map((value) => ({
                value,
                label: t(RESOURCE_KIND_LABELS[value]),
              }))}
            />
          </div>
        )}
      </div>

      {resources.isPending ? (
        <SkeletonCard />
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-ds-border p-4">
          <EmptyState
            icon={<FolderOpen className="h-5 w-5 text-ds-ink-3" />}
            title={search || kind ? t("Sin resultados") : t("Todavía no hay materiales")}
            description={
              search || kind
                ? t("Nada coincide con ese filtro.")
                : t("Cuando la casa publique formación, la encuentras aquí.")
            }
          />
        </div>
      ) : (
        /*
         * UNA rejilla corrida que LLENA el ancho (Anahí, 27-ago: "una
         * card en la primera fila y en cada columna, sin dejar todo ese
         * espacio"): fuera los grupos con su columna huérfana. Al
         * filtrar, el rótulo dice qué se está viendo y cuántos son.
         */
        <section>
          {(kind || search) && (
            <h2 className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-ds-label text-ds-ink-3">
              <Filter className="h-3.5 w-3.5" />
              {kind ? t(RESOURCE_KIND_LABELS[kind as ResourceKind]) : t("Resultados")}
              <span className="font-medium text-ds-ink-4">({filtered.length})</span>
            </h2>
          )}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filtered.map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

const CARD_CLASS =
  "group relative block h-56 w-full overflow-hidden rounded-3xl border border-ds-border text-left shadow-ds-float transition hover:-translate-y-0.5 hover:shadow-ds-card-hv motion-reduce:transform-none";

/**
 * La card de la biblioteca: la portada llena la tarjeta, un degradado
 * oscuro ancla el texto abajo —título, tipo, fecha— y la descarga vive
 * en la esquina. Es la card del sistema anterior con la ropa del DS.
 *
 * Dos comportamientos, según de dónde salga el material: si es un
 * ENLACE (video, link) abre pestaña; si es un ARCHIVO guardado (PDF,
 * imagen) lo pide en el clic y lo baja — los bytes no viajan al pintar
 * la biblioteca, que serían decenas de MB por pantalla.
 */
function ResourceCard({ resource }: { resource: Resource }) {
  const { t } = useTranslation();
  const Icon = KIND_ICONS[resource.kind];
  const [bajando, setBajando] = useState(false);

  /*
   * Una IMAGEN es su propia portada: por eso el alta no le pide otra.
   * Si no trae `imageUrl` (las importadas del sistema viejo sí), la
   * card pide su archivo — y solo ella, no toda la rejilla.
   */
  const portadaPropia = useQuery({
    queryKey: ["resource-file", resource.id],
    queryFn: () => getResourceFile(resource.id),
    enabled: resource.kind === "imagen" && !resource.imageUrl && Boolean(resource.file),
    staleTime: Infinity,
  });
  const cover = resource.imageUrl ?? portadaPropia.data?.file?.dataUrl;

  async function bajar() {
    setBajando(true);
    try {
      await downloadResource(resource.id);
    } finally {
      setBajando(false);
    }
  }

  const inner = (
    <>
      {/* La portada — o el placeholder de la casa. */}
      {cover ? (
        <img
          src={cover}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03] motion-reduce:transform-none"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-ds-mist via-ds-cream-2 to-ds-mist">
          <Icon className="h-14 w-14 text-ds-violet-pop/40" />
        </div>
      )}

      {/* El velo que hace legible el texto sobre CUALQUIER portada. */}
      <div className="absolute inset-0 bg-gradient-to-t from-ds-ink/80 via-ds-ink/25 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4">
        <div className="min-w-0">
          <p className="truncate text-[16px] font-semibold text-white">{resource.title}</p>
          {resource.description && (
            <p className="mt-0.5 line-clamp-1 text-[12.5px] text-white/75">
              {resource.description}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11.5px] font-semibold text-white/85">
            <span className="inline-flex items-center gap-1">
              <Icon className="h-3.5 w-3.5" />
              {t(RESOURCE_KIND_LABELS[resource.kind])}
            </span>
            <span>{formatOrderDate(resource.createdAt)}</span>
          </div>
        </div>
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition group-hover:bg-ds-violet-pop"
        >
          {bajando ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : resource.file ? (
            <Download className="h-4 w-4" />
          ) : (
            <ExternalLink className="h-4 w-4" />
          )}
        </span>
      </div>
    </>
  );

  if (resource.file) {
    return (
      <button type="button" onClick={() => void bajar()} className={CARD_CLASS}>
        {inner}
        <span className="sr-only">{t("Descargar")}</span>
      </button>
    );
  }

  return (
    <a href={resource.url} target="_blank" rel="noreferrer" className={CARD_CLASS}>
      {inner}
    </a>
  );
}
